import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Terminal, Plus, Package, Server, Pencil, Trash2,
  ExternalLink, Loader2, AlertTriangle, BookOpen,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMySkills, useDeleteSkill } from '@/hooks/useSkills';
import { useToast } from '@/hooks/useToast';
import { formatPrice, type Skill } from '@/lib/skills';

export default function MySkills() {
  useSeoMeta({
    title: 'My Skills - SkillForge',
    description: 'Manage your published skills and MCP servers.',
  });

  const { user } = useCurrentUser();
  const { data: skills, isLoading } = useMySkills();
  const npub = user ? nip19.npubEncode(user.pubkey) : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
              <Terminal className="w-4 h-4 text-orange-400" />
            </div>
            <span className="font-semibold text-lg tracking-tight">SkillForge</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
            <Link to="/connect-agent" className="hover:text-foreground transition-colors">Connect Agent</Link>
            <Link to="/publish" className="hover:text-foreground transition-colors opacity-50 hover:opacity-100 text-xs">Publish</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      {!user ? (
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6 border border-white/[0.08]">
              <Terminal className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-bold mb-4">Sign in to manage your skills</h1>
            <p className="text-muted-foreground mb-8">
              Connect your Nostr identity to view and edit your published skills.
            </p>
            <LoginArea />
          </div>
        </main>
      ) : (
        <main className="container mx-auto px-4 py-10">
          <div className="max-w-4xl mx-auto">
            {/* Page header */}
            <div className="flex items-center justify-between mb-10">
              <div>
                <h1 className="text-4xl font-bold mb-2">My Skills</h1>
                <p className="text-muted-foreground">
                  Manage your published skills and MCP servers.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {npub && (
                  <Link to={`/profile/${npub}`}>
                    <Button variant="outline" size="sm" className="gap-2 border-white/[0.1]">
                      <BookOpen className="w-4 h-4" />
                      Public Profile
                    </Button>
                  </Link>
                )}
                <Link to="/publish">
                  <Button size="sm" className="gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0 shadow-lg shadow-orange-500/20">
                    <Plus className="w-4 h-4" />
                    Publish New
                  </Button>
                </Link>
              </div>
            </div>

            {/* Skills list */}
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkillRowSkeleton key={i} />
                ))}
              </div>
            ) : !skills || skills.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-16 text-center">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
                  <h2 className="text-lg font-semibold mb-2">No skills published yet</h2>
                  <p className="text-muted-foreground text-sm mb-6">
                    Share your first AI agent skill or MCP server with the community.
                  </p>
                  <Link to="/publish">
                    <Button className="gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0">
                      <Plus className="w-4 h-4" />
                      Publish a Skill
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {skills.map((skill) => (
                  <SkillRow key={skill.id} skill={skill} />
                ))}
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  );
}

function SkillRow({ skill }: { skill: Skill }) {
  const { mutateAsync: deleteSkill, isPending: isDeleting } = useDeleteSkill();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteSkill(skill);
      toast({ title: 'Skill deleted', description: `"${skill.name}" has been removed.` });
    } catch {
      toast({ title: 'Delete failed', description: 'Could not delete this skill.', variant: 'destructive' });
    }
    setConfirmOpen(false);
  };

  return (
    <div className="glass-card rounded-2xl p-5 flex items-center gap-5 group hover:glass-card-hover transition-all duration-200">
      {/* Icon */}
      <div className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center border border-white/[0.08] ${
        skill.type === 'mcp'
          ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 text-emerald-400'
          : 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 text-orange-400'
      }`}>
        {skill.type === 'mcp' ? <Server className="w-5 h-5" /> : <Package className="w-5 h-5" />}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-0.5">
          <span className="font-semibold truncate">{skill.name}</span>
          <Badge variant="outline" className="text-[10px] font-mono shrink-0 bg-white/[0.02] border-white/[0.08]">
            v{skill.version}
          </Badge>
          <Badge
            variant={skill.price > 0 ? 'default' : 'secondary'}
            className={`text-[10px] shrink-0 ${skill.price > 0 ? 'bg-gradient-to-r from-orange-500/80 to-amber-500/80 border-0' : 'bg-white/[0.05] border-white/[0.08]'}`}
          >
            {formatPrice(skill.price)}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground truncate">{skill.description}</p>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-[10px] text-muted-foreground/60 font-mono">{skill.identifier}</span>
          <span className="text-[10px] text-muted-foreground/40">·</span>
          <span className="text-[10px] text-muted-foreground/60">
            {new Date(skill.createdAt * 1000).toLocaleDateString()}
          </span>
          {skill.tags.length > 0 && (
            <>
              <span className="text-[10px] text-muted-foreground/40">·</span>
              <div className="flex gap-1">
                {skill.tags.slice(0, 3).map((t) => (
                  <span key={t} className="text-[10px] bg-white/[0.04] border border-white/[0.06] px-1.5 py-0.5 rounded text-muted-foreground/70">
                    {t}
                  </span>
                ))}
                {skill.tags.length > 3 && (
                  <span className="text-[10px] text-muted-foreground/40">+{skill.tags.length - 3}</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Link to={`/skill/${skill.pubkey}/${skill.identifier}`}>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white/[0.06]" title="View">
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        </Link>
        <Link to={`/edit/${skill.identifier}`}>
          <Button size="sm" variant="ghost" className="h-8 gap-1.5 px-3 hover:bg-orange-500/10 hover:text-orange-400 transition-colors">
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </Button>
        </Link>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1.5 px-3 hover:bg-red-500/10 hover:text-red-400 transition-colors"
              title="Delete"
            >
              {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="glass-card border-white/[0.12]">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Delete "{skill.name}"?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">
                This publishes a NIP-09 deletion event to Nostr relays. Relays that honour deletions will remove the skill, but it may persist on relays that don't.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/[0.1]">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-500/80 hover:bg-red-500 border-0 text-white"
              >
                {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function SkillRowSkeleton() {
  return (
    <div className="glass-card rounded-2xl p-5 flex items-center gap-5">
      <Skeleton className="w-11 h-11 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-72" />
      </div>
      <Skeleton className="h-8 w-20 rounded-lg" />
    </div>
  );
}
